import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuth } from "../auth/AuthContext";

let wishlistCache = null;
let wishlistRequest = null;

function productKey(product) {
  return String(product?._id || product?.id || product?.sku || "");
}

function wishlistPayload(product) {
  return {
    productId: productKey(product),
    name: product.name,
    sku: product.sku,
    price: Number(product.price || 0),
    image: product.image,
  };
}

function findWishlistMatch(items, key, sku) {
  return (items || []).find((item) => String(item.productId) === key || (sku && item.sku === sku));
}

async function getWishlistItems() {
  if (wishlistCache) return wishlistCache;
  if (!wishlistRequest) {
    wishlistRequest = api.get("/wishlist")
      .then((data) => {
        wishlistCache = data.items || [];
        return wishlistCache;
      })
      .finally(() => {
        wishlistRequest = null;
      });
  }
  return wishlistRequest;
}

export function useWishlistActions(product, onMessage) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [savedItem, setSavedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = productKey(product);

  const loadWishlistState = useCallback(async () => {
    if (!isAuthenticated || !key) {
      setSavedItem(null);
      return;
    }
    try {
      const items = await getWishlistItems();
      setSavedItem(findWishlistMatch(items, key, product?.sku) || null);
    } catch {
      setSavedItem(null);
    }
  }, [isAuthenticated, key, product?.sku]);

  useEffect(() => {
    loadWishlistState();
  }, [loadWishlistState]);

  const toggleWishlist = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!product) return;
    if (!isAuthenticated) {
      onMessage?.("Please login to save products.");
      navigate("/account");
      return;
    }

    setLoading(true);
    try {
      if (savedItem?._id) {
        await api.delete(`/wishlist/${savedItem._id}`);
        wishlistCache = (wishlistCache || []).filter((item) => item._id !== savedItem._id);
        setSavedItem(null);
        onMessage?.("Removed from wishlist.");
      } else {
        const data = await api.post("/wishlist", wishlistPayload(product));
        wishlistCache = [data.item, ...(wishlistCache || []).filter((item) => item._id !== data.item?._id)];
        setSavedItem(data.item);
        onMessage?.("Added to wishlist.");
      }
    } catch (err) {
      onMessage?.(err.message || "Wishlist update failed.");
    } finally {
      setLoading(false);
    }
  };

  return {
    isSaved: Boolean(savedItem),
    loading,
    reloadWishlistState: loadWishlistState,
    toggleWishlist,
  };
}
